import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Combobox } from '@/components/ui/combobox';
import { Textarea } from '@/components/ui/textarea';
import { trpc } from '@/lib/trpc';
import { formatCurrency } from '@/lib/utils';
import { showSuccessToast, showErrorToast } from '@/hooks/use-mutation-with-toast';

interface FormData {
  amount: number;
  paymentMethod: string;
  referenceNumber: string;
  notes: string;
}

interface InstallmentPaymentModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  installmentId: string;
  installmentDescription: string;
  remainingAmount: number;
  currency: string;
  userId?: string;
  onSuccess: () => void;
}

const paymentMethods = [
  { value: 'CASH', label: 'Cash' },
  { value: 'CREDIT_CARD', label: 'Credit Card' },
  { value: 'DEBIT_CARD', label: 'Debit Card' },
  { value: 'BANK_TRANSFER', label: 'Bank Transfer' },
  { value: 'PAYPAL', label: 'PayPal' },
  { value: 'STRIPE', label: 'Stripe' },
  { value: 'OTHER', label: 'Other' },
];

export function InstallmentPaymentModal({
  open,
  onOpenChange,
  installmentId,
  installmentDescription,
  remainingAmount,
  currency,
  userId,
  onSuccess,
}: InstallmentPaymentModalProps) {
  const { t } = useTranslation('paymentPlans');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    reset,
    formState: { errors },
  } = useForm<FormData>({
    defaultValues: {
      amount: remainingAmount,
      paymentMethod: 'CREDIT_CARD',
      referenceNumber: '',
      notes: '',
    },
  });

  const paymentMethod = watch('paymentMethod');

  const recordPaymentMutation = trpc.paymentPlan.recordPayment.useMutation({
    onSuccess: () => {
      showSuccessToast(t('installment.recordPayment') + ' - Success');
      reset();
      onOpenChange(false);
      onSuccess();
    },
    onError: (error) => {
      setIsSubmitting(false);
      showErrorToast(error.message);
    },
  });

  const onSubmit = (data: FormData) => {
    setIsSubmitting(true);
    recordPaymentMutation.mutate({
      installmentId,
      amount: data.amount,
      paymentMethod: data.paymentMethod as 'CASH' | 'CREDIT_CARD' | 'DEBIT_CARD' | 'BANK_TRANSFER' | 'PAYPAL' | 'CHEQUE' | 'OTHER',
      referenceNumber: data.referenceNumber || undefined,
      notes: data.notes || undefined,
      userId,
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t('installment.recordPayment')}</DialogTitle>
          <DialogDescription>
            {installmentDescription} - {formatCurrency(remainingAmount, currency)} remaining
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="amount">{t('installment.amount')} *</Label>
            <div className="relative">
              <Input
                id="amount"
                type="number"
                step="0.01"
                min="0.01"
                max={remainingAmount}
                {...register('amount', {
                  required: 'Amount is required',
                  valueAsNumber: true,
                  min: { value: 0.01, message: 'Amount must be greater than 0' },
                  max: { value: remainingAmount, message: `Amount cannot exceed ${formatCurrency(remainingAmount, currency)}` },
                })}
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground">
                {currency}
              </span>
            </div>
            {errors.amount && (
              <p className="text-sm text-red-500">{errors.amount.message}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label>Payment Method *</Label>
            <Combobox
              options={paymentMethods.map((method) => ({
                value: method.value,
                label: method.label,
              }))}
              value={paymentMethod}
              onValueChange={(value) => setValue('paymentMethod', value)}
              placeholder="Select payment method"
              searchPlaceholder="Search..."
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="referenceNumber">Reference Number</Label>
            <Input
              id="referenceNumber"
              {...register('referenceNumber')}
              placeholder="e.g., Transaction ID"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="notes">Notes</Label>
            <Textarea
              id="notes"
              {...register('notes')}
              placeholder="Optional notes..."
              rows={2}
            />
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {t('installment.recordPayment')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
